import { texture } from 'three/tsl';
import * as THREE from 'three/webgpu';
import { octDecodeCpu, octEncode } from '../render/octahedral';

type N = any;

/** Texels per side of the flare map (octahedral). */
const SIZE = 64;
/** Regions that flare on their own: patches about a third of the world's radius across. */
const REGIONS = 140;
/** Regions blended at each point (the nearest ones). */
const BLEND = 4;
/** Angular spread of a region's weight (radians), from the regions' spacing. */
const SPREAD = 0.55 * Math.sqrt((4 * Math.PI) / REGIONS);

/** A flare: when it started, how strong it gets, how fast it rises and dies down (s). */
interface Flare {
  start: number;
  peak: number;
  rise: number;
  decay: number;
}

interface Region {
  dir: THREE.Vector3;
  rnd: () => number;
  cur: Flare;
  /** The flare before (still dying down when the next one starts). */
  prev: Flare;
  next: number;
  /** Its flicker: rate (Hz) and phase. */
  hz: number;
  phase: number;
  value: number;
}

/** mulberry32: a small seeded generator (0 ≤ r < 1). */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A flare's level `t` seconds into the clock (0 before it starts). */
function level(f: Flare, t: number): number {
  const tau = t - f.start;
  if (tau < 0 || f.peak === 0) return 0;
  // A fast surge (easing into its peak), then a slow exponential dying down.
  const env = tau < f.rise ? 1 - (1 - tau / f.rise) ** 2 : Math.exp(-(tau - f.rise) / f.decay);
  return f.peak * env;
}

const _d = new THREE.Vector3();
const _idx = new Int32Array(BLEND);
const _w = new Float32Array(BLEND);
const _best = new Float64Array(BLEND);
const _which = new Int32Array(BLEND);

/**
 * Lava that flares region by region: each of REGIONS patches of the world surges at its own
 * random times and dies down again (a fast rise, a slow decay, now and then an eruption), so
 * the world flickers like a field of vents and no two places keep time. The CPU runs the
 * flares and writes them into a small octahedral map every frame; shaders read it with one
 * texture fetch (`sample`). The level is 0 where all is quiet and about 1 at a typical peak
 * (eruptions go higher).
 */
export class GlowFlares {
  readonly texture: THREE.DataTexture;
  /** Mean level over the world. */
  average = 0;
  private readonly regions: Region[] = [];
  /** For each texel, its BLEND nearest regions and their weights. */
  private readonly near = new Int32Array(SIZE * SIZE * BLEND);
  private readonly weight = new Float32Array(SIZE * SIZE * BLEND);
  private readonly data = new Uint16Array(SIZE * SIZE);

  constructor(seed: number) {
    const rnd = seeded(seed * 7919 + 17);
    // Fibonacci points, turned at random: evenly spread regions.
    const axis = new THREE.Vector3(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5).normalize();
    const turn = rnd() * Math.PI * 2;
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < REGIONS; i++) {
      const y = 1 - (2 * (i + 0.5)) / REGIONS;
      const r = Math.sqrt(1 - y * y);
      const dir = new THREE.Vector3(r * Math.cos(i * golden), y, r * Math.sin(i * golden)).applyAxisAngle(
        axis,
        turn,
      );
      const own = seeded(seed * 104729 + i * 7907 + 3);
      const idle: Flare = { start: 0, peak: 0, rise: 1, decay: 1 };
      this.regions.push({
        dir,
        rnd: own,
        cur: idle,
        prev: { ...idle },
        // Out of step from the start.
        next: own() * 20,
        hz: 0.7 + 1.6 * own(),
        phase: own() * Math.PI * 2,
        value: 0,
      });
    }
    for (let j = 0; j < SIZE; j++) {
      for (let i = 0; i < SIZE; i++) {
        octDecodeCpu((i + 0.5) / SIZE, (j + 0.5) / SIZE, _d);
        this.nearest(_d, (j * SIZE + i) * BLEND, this.near, this.weight);
      }
    }
    this.texture = new THREE.DataTexture(this.data, SIZE, SIZE, THREE.RedFormat, THREE.HalfFloatType);
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = false;
    this.texture.name = 'lava flares';
    this.update(0);
  }

  /** The level at hollow-local unit direction `dir`, as a node. */
  sample(dir: N): N {
    return texture(this.texture, octEncode(dir)).r;
  }

  /** The level at a hollow-local unit direction, on the CPU. */
  valueAt(dir: THREE.Vector3): number {
    this.nearest(dir, 0, _idx, _w);
    let v = 0;
    for (let k = 0; k < BLEND; k++) v += this.regions[_idx[k]].value * _w[k];
    return v;
  }

  /** Run every region's flares to `clock` (seconds; it only moves forward) and write the map. */
  update(clock: number): void {
    let sum = 0;
    for (const r of this.regions) {
      // Start the flares that are due (several if time jumped: hold T).
      while (clock >= r.next) {
        const spent = r.prev;
        r.prev = r.cur;
        r.cur = spent;
        // Short, sharp surges, and one in ten an eruption that burns for a while. Between them
        // a region lies quiet: about three quarters of the world is dark at any moment.
        const big = r.rnd() < 0.1;
        spent.start = r.next;
        spent.peak = big ? 1.6 + 0.8 * r.rnd() : 0.6 + 0.5 * r.rnd();
        spent.rise = 0.4 + r.rnd();
        spent.decay = big ? 5 + 5 * r.rnd() : 1.5 + 3 * r.rnd();
        // Random waits, mostly short: 5 to 40 s.
        r.next += 5 + 35 * r.rnd() ** 2;
      }
      const v = Math.max(level(r.cur, clock), level(r.prev, clock));
      // A flaring vent flickers.
      r.value = v * (1 + 0.12 * Math.sin(clock * r.hz * Math.PI * 2 + r.phase));
      sum += r.value;
    }
    this.average = sum / this.regions.length;
    const regions = this.regions;
    const data = this.data;
    for (let t = 0; t < SIZE * SIZE; t++) {
      let v = 0;
      for (let k = 0; k < BLEND; k++)
        v += regions[this.near[t * BLEND + k]].value * this.weight[t * BLEND + k];
      data[t] = THREE.DataUtils.toHalfFloat(v);
    }
    this.texture.needsUpdate = true;
  }

  /** The BLEND regions nearest `dir`, and their weights (summing to 1), at `at` in the arrays. */
  private nearest(dir: THREE.Vector3, at: number, idx: Int32Array, w: Float32Array): void {
    const best = _best.fill(-2);
    const which = _which.fill(0);
    for (let i = 0; i < this.regions.length; i++) {
      const c = this.regions[i].dir.dot(dir);
      if (c <= best[BLEND - 1]) continue;
      let k = BLEND - 1;
      while (k > 0 && best[k - 1] < c) {
        best[k] = best[k - 1];
        which[k] = which[k - 1];
        k--;
      }
      best[k] = c;
      which[k] = i;
    }
    let sum = 0;
    for (let k = 0; k < BLEND; k++) {
      const angle = Math.acos(Math.min(1, Math.max(-1, best[k])));
      const wk = Math.exp(-((angle / SPREAD) ** 2));
      idx[at + k] = which[k];
      w[at + k] = wk;
      sum += wk;
    }
    for (let k = 0; k < BLEND; k++) w[at + k] /= sum;
  }
}
